import {type ApolloError} from '@apollo/client';
import {Banner} from '@wandb/components';
import React from 'react';

const DEFAULT_MESSAGE = 'Failed to load some metrics';

/**
 * Failure notice shared by the metric-search-backed pickers. Without it a
 * failed query is indistinguishable from a project with no matching keys.
 *
 * Takes both failures a picker can have and decides between them here, so the
 * pickers don't each restate the rules:
 *
 * - `searchErrorMessage` is the backend rejecting the typed pattern as a regex
 *   it can't run. It wins, and gets no Retry: rerunning a rejected pattern
 *   returns the same rejection, so the pattern is what has to change.
 * - `error` is a failure a retry could clear, and shows the generic message.
 *
 * Renders nothing when neither is set, so callers can drop it in
 * unconditionally.
 */
export const MetricSearchErrorNotice = ({
  searchErrorMessage,
  error,
  onRetry,
}: {
  searchErrorMessage?: string;
  error?: ApolloError;
  onRetry?: () => void;
}) => {
  if (searchErrorMessage == null && error == null) {
    return null;
  }

  const isRejection = searchErrorMessage != null;

  return (
    <div data-test="metric-search-error">
      <Banner isDismissible={false} size="sm" variant="error">
        <Banner.Body>{searchErrorMessage ?? DEFAULT_MESSAGE}</Banner.Body>
        {!isRejection && onRetry != null && (
          <Banner.Action aria-label="Retry loading metrics" onPress={onRetry}>
            Retry
          </Banner.Action>
        )}
      </Banner>
    </div>
  );
};
